import { router } from "@inertiajs/react"
import {
  ArrowDownToLine,
  Check,
  Download,
  KeyRound,
  Loader2,
  Lock,
  Upload,
} from "lucide-react"
import { type FormEvent, useState } from "react"

import { FileDropzone } from "@/components/FileDropzone"
import { PdfThumbnail } from "@/components/PdfThumbnail"
import { PublicFooter } from "@/components/public-footer"
import { PublicHeader } from "@/components/public-header"
import { RelatedTools } from "@/components/related-tools"
import { SEO } from "@/components/seo"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { PasswordInput } from "@/components/ui/password-input"
import { useOperationGuard } from "@/hooks/useOperationGuard"
import { type PdfFile, usePdfThumbnails } from "@/hooks/usePdfThumbnails"

const howItWorks = [
  { icon: Upload, text: "Upload PDF" },
  { icon: KeyRound, text: "Set a password" },
  { icon: ArrowDownToLine, text: "Download protected PDF" },
]

interface Props {
  limits: {
    daily_remaining: number
    max_file_size_mb: number
    max_pages: number
  }
  authenticated: boolean
}

export default function Protect({ limits, authenticated }: Props) {
  const [file, setFile] = useState<PdfFile | null>(null)
  const [rawFile, setRawFile] = useState<File | null>(null)
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { generateThumbnails, loading: loadingThumbnails } = usePdfThumbnails()
  const {
    remainingOperations,
    completed,
    guardOperation,
    markCompleted,
    Modals,
  } = useOperationGuard({
    dailyRemaining: limits.daily_remaining,
    authenticated,
  })

  const passwordsMatch = password === confirmPassword
  const canSubmit = file && password.length > 0 && passwordsMatch && !processing

  const handleFilesAdded = async (newFiles: File[]) => {
    const first = newFiles[0]
    if (!first) return

    setRawFile(first)
    const pdfFile = await generateThumbnails(first)
    setFile(pdfFile)
    setError(null)
  }

  const handleRemoveFile = () => {
    setFile(null)
    setRawFile(null)
    setPassword("")
    setConfirmPassword("")
    setError(null)
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!canSubmit || !rawFile) return
    if (!guardOperation()) return

    setProcessing(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append("file", rawFile)
      formData.append("password", password)

      const csrfToken = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]',
      )?.content

      const response = await fetch("/protect-pdf", {
        method: "POST",
        headers: {
          "X-CSRF-Token": csrfToken ?? "",
        },
        body: formData,
      })

      if (response.status === 429) {
        setError("Daily operation limit reached. Upgrade your plan for more.")
        return
      }

      if (!response.ok) {
        setError("Failed to protect PDF. Please try again.")
        return
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `${rawFile.name.replace(".pdf", "")}_protected.pdf`
      a.click()
      URL.revokeObjectURL(url)
      markCompleted()
      router.flushAll()
    } catch {
      setError("Something went wrong. Please try again.")
    } finally {
      setProcessing(false)
    }
  }

  return (
    <>
      <SEO
        title="Password Protect PDF - Free Encryption | StitchPDF"
        description="Add password protection to your PDF files for free. Secure your documents with AES-256 encryption. No software installation required."
        canonical="https://stitchpdf.com/protect-pdf"
        ogTitle="Protect PDF with Password - StitchPDF"
        ogDescription="Add password protection to your PDF files for free."
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Protect PDF - StitchPDF",
            description:
              "Add password protection to PDF files with AES-256 encryption.",
            applicationCategory: "UtilitiesApplication",
            operatingSystem: "Web Browser",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to Password Protect a PDF",
            description: "Secure your PDF with password encryption.",
            step: [
              {
                "@type": "HowToStep",
                name: "Upload PDF",
                text: "Select the PDF file you want to protect.",
              },
              {
                "@type": "HowToStep",
                name: "Set password",
                text: "Enter and confirm your password.",
              },
              {
                "@type": "HowToStep",
                name: "Download protected PDF",
                text: "Click Protect & Download to get your encrypted PDF.",
              },
            ],
          })}
        </script>
      </SEO>

      <div className="bg-background flex min-h-screen flex-col">
        <PublicHeader />

        <main className="container mx-auto flex-1 px-4 py-8">
          <div className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3">
              <Lock className="text-primary h-8 w-8" aria-hidden="true" />
              <h1 className="text-2xl font-bold">Protect PDF</h1>
            </div>
            <p className="text-muted-foreground mt-2">
              Add password protection to your PDF
            </p>

            <div className="mt-6 flex items-center justify-center gap-6 text-sm">
              {howItWorks.map((step, i) => {
                const hasFile = file !== null
                const hasPassword = password.length > 0 && passwordsMatch
                const isComplete =
                  completed ||
                  (hasFile && i === 0) ||
                  (hasPassword && i === 1) ||
                  (processing && i <= 1)
                const isActive =
                  !completed &&
                  ((processing && i === 2) ||
                    (!processing &&
                      ((hasFile && !hasPassword && i === 1) ||
                        (hasPassword && i === 2))))
                return (
                  <div
                    key={step.text}
                    className={`flex items-center gap-2 ${
                      isComplete
                        ? "text-green-700 dark:text-green-400"
                        : isActive
                          ? "text-primary font-medium"
                          : "text-muted-foreground"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                        isComplete
                          ? "bg-green-200 text-green-700 dark:bg-green-900 dark:text-green-400"
                          : isActive
                            ? "bg-primary text-primary-foreground"
                            : "bg-primary/10 text-primary"
                      }`}
                    >
                      {isComplete ? (
                        <Check className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        i + 1
                      )}
                    </span>
                    <step.icon className="h-4 w-4" aria-hidden="true" />
                    <span>{step.text}</span>
                  </div>
                )
              })}
            </div>

            {!file && (
              <div className="mt-8">
                <FileDropzone
                  onFilesAdded={(files) => void handleFilesAdded(files)}
                  multiple={false}
                />
              </div>
            )}

            {loadingThumbnails && (
              <div className="text-muted-foreground mt-4 flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading PDF...
              </div>
            )}

            {file && (
              <form onSubmit={(e) => void handleSubmit(e)} className="mt-8">
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    {file.name} ({file.pageCount} pages)
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {remainingOperations} operations remaining today
                  </p>
                </div>

                <div className="mt-4 flex flex-wrap gap-4">
                  <PdfThumbnail
                    src={file.thumbnails[0]}
                    label={file.name}
                    onRemove={handleRemoveFile}
                  />
                </div>

                <div className="mt-8 grid gap-4 sm:max-w-sm">
                  <div className="grid gap-2">
                    <Label htmlFor="password">Password</Label>
                    <PasswordInput
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="confirm-password">Confirm password</Label>
                    <PasswordInput
                      id="confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm password"
                    />
                    {confirmPassword.length > 0 && !passwordsMatch && (
                      <p className="text-destructive text-sm">
                        Passwords don&apos;t match
                      </p>
                    )}
                  </div>
                </div>

                {error && (
                  <p className="text-destructive mt-4 text-sm">{error}</p>
                )}

                <div className="mt-8 flex gap-4">
                  <Button type="submit" disabled={!canSubmit} className="gap-2">
                    {processing ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Protecting...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4" />
                        Protect & Download
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}

            <RelatedTools currentTool="protect-pdf" />

            <section className="mt-16 border-t pt-12">
              <h2 className="text-xl font-semibold">About This Tool</h2>
              <div className="text-muted-foreground mt-4 space-y-4 text-sm leading-relaxed">
                <p>
                  Add a password to your PDF. Recipients need the password to
                  open it. We use AES-256 encryption, the same standard banks
                  and governments use.
                </p>
                <p>
                  This tool requires server processing for encryption. We delete
                  your file immediately after generating the protected version.
                </p>
                <h3 className="text-foreground pt-4 font-medium">Features</h3>
                <ul className="list-inside list-disc space-y-2">
                  <li>
                    <strong>AES-256 encryption:</strong> Standard used by banks
                  </li>
                  <li>
                    <strong>Works everywhere:</strong> Opens in any PDF reader
                  </li>
                  <li>
                    <strong>Immediate deletion:</strong> File removed after
                    processing
                  </li>
                  <li>
                    <strong>No password storage:</strong> We never keep your
                    password
                  </li>
                </ul>
                <h3 className="text-foreground pt-4 font-medium">Use Cases</h3>
                <p>
                  Protect financial documents before emailing. Secure contracts.
                  Add passwords to tax returns or medical records.
                </p>
              </div>
            </section>
          </div>
        </main>
        <PublicFooter />
      </div>

      <Modals />
    </>
  )
}
